import {
    EventrDate,
    ProfileShareSection,
    regexPhone,
    UpdateUserDTO,
} from '@eventr/common'
import { z, ZodObject, ZodSchema } from 'zod'
import i18n from '../../../i18n/i18next'
import { bs } from '../../../state/bound.store'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'

export async function updateUserData(
    userDetails: UpdateUserDTO
): Promise<boolean> {
    loadingSignal.value = true

    if (!bs().getCurrentUser()?.id) {
        loadingSignal.value = false
        return true
    }
    try {
        await bs().updateRequest(userDetails)
    } catch (error) {
        loadingSignal.value = false

        return false
    }
    await bs().fetchUsers()
    loadingSignal.value = false
    return true
}

const regexName = new RegExp(/^\S.*/)

function startOfDay(unixTimestamp: number | Date) {
    const date = new Date(unixTimestamp * 1000)
    date.setHours(0, 0, 0, 0)
    return Math.floor(date.getTime() / 1000)
}

function setDate(date: any) {
    if (date) {
        const selectedDate = new EventrDate(date)
            .clearTimezone()
            .setFromObject({ hour: 12, minute: 0 })
        return selectedDate.getJsDate()
    }
    return null
}

const buildAdditionalInfoSchema = (
    sections: ProfileShareSection[]
): ZodObject<any> => {
    const schema: { [key: string]: ZodSchema } = {}

    sections.forEach((section) => {
        const sectionSchema: { [key: string]: ZodSchema } = {}

        section.fields?.forEach((field) => {
            let fieldSchema: ZodSchema

            switch (field.type) {
                case 'text':
                    fieldSchema = z.string().max(100, {
                        message: i18n.t('account.validations.exceeds-100'),
                    })
                    break
                case 'multiLineText':
                    fieldSchema = z.string().max(1000, {
                        message: i18n.t('account.validations.exceeds-1000'),
                    })
                    break
                case 'list':
                    fieldSchema = z.array(z.string())
                    break
                case 'countrySelect':
                    fieldSchema = z.string()
                    break
                case 'dateSelect':
                    fieldSchema = z
                        .any()
                        .transform((value) => setDate(value))
                        .refine(() => true)
                    break
                case 'dateSelectPast':
                    fieldSchema = z
                        .any()
                        .transform((value) => {
                            return setDate(value)
                        })
                        .refine(
                            (value) => {
                                if (!value) return true
                                const selectedDate = startOfDay(value)
                                const today = startOfDay(new Date())
                                return selectedDate < today
                            },
                            {
                                message: i18n.t(
                                    'account.validations.date-before'
                                ),
                            }
                        )
                    break
                case 'dateSelectFuture':
                    fieldSchema = z
                        .any()
                        .transform((value) => {
                            return setDate(value)
                        })
                        .refine(
                            (value) => {
                                if (!value) return true
                                const selectedDate = startOfDay(value)
                                const today = startOfDay(new Date())
                                return selectedDate > today
                            },
                            {
                                message: i18n.t(
                                    'account.validations.date-after'
                                ),
                            }
                        )
                    break
                default:
                    fieldSchema = z.any().optional()
            }
            sectionSchema[`field_${field.id}`] = fieldSchema.optional()
        })
        if (section.name) {
            schema[`section_${section.id}`] = z.object(sectionSchema).optional()
        }
    })
    return z.object(schema)
}

export function UserDetailsSchema(sections?: ProfileShareSection[]) {
    const additionalInfoSchema = buildAdditionalInfoSchema(sections ?? [])

    return z.object({
        title: z
            .string()
            .max(50, { message: i18n.t('account.titleTooLong') })
            .optional(),
        firstName: z
            .string()
            .min(1)
            .max(100, { message: i18n.t('account.firstNameTooLong') })
            .regex(regexName),
        lastName: z
            .string()
            .min(1)
            .max(200, { message: i18n.t('account.lastNameTooLong') })
            .regex(regexName),
        phoneNumber: z.string().max(30).regex(regexPhone).optional(),
        email: z.string().email(),
        dateOfBirth: z
            .any()
            .transform((value) => {
                return setDate(value)
            })
            .refine(
                (value) => {
                    if (!value) return true
                    const selectedDate = startOfDay(value)
                    const today = startOfDay(new Date())
                    return selectedDate < today
                },
                { message: i18n.t('account.invalidDate') }
            )
            .optional(),
        countryCode: z.string(),
        profilePic: z
            .union([
                z.custom<File>((value) => value instanceof File, {
                    message: i18n.t('account.invalidProfilePic'),
                }),
                z
                    .string()
                    .url({ message: i18n.t('account.invalidProfilePicURL') }),
            ])
            .optional(),
        additionalInfo: additionalInfoSchema.optional(),
        targetOrg: z.string().optional(),
        sharedProperties: z.string().array().optional(),
        marketingOptIn: z.union([z.number(), z.boolean()]).optional(),
    })
}

export type UserValidationSchema = z.infer<ReturnType<typeof UserDetailsSchema>>

export const filterAndExtractIds = (sections: any[]) => {
    const ids: string[] = []
    sections.forEach((section) => {
        ids.push(`${section.id}`)
    })
    return ids
}

export const findMissingData = (
    requiredData: string[],
    providedData: string[]
) => {
    const firstSet = new Set(requiredData)
    const secondSet = new Set(providedData)
    const missingNumbers: string[] = []

    firstSet.forEach((num) => {
        if (!secondSet.has(num)) {
            missingNumbers.push(num)
        }
    })

    return missingNumbers
}
